/* eslint-disable @typescript-eslint/no-empty-function */
/* eslint-disable @typescript-eslint/no-use-before-define */
/* eslint-disable @typescript-eslint/no-explicit-any */

/**
 * Copyright (C) SaigonMD, Inc - All Rights Reserved
 * Licensed under the MIT license.
 * Written by Tran Quan <tranquan221b@gmail.com>, July 2018
 */
/**
 * Log helper using Reactotron https://github.com/infinitered/reactotron
 * - log faster than console because no need to turn-on remote debug
 * - able to customize the message
 */
import apisaucePlugin from 'reactotron-apisauce'
import { mst } from 'reactotron-mst'
import Reactotron, { trackGlobalErrors } from 'reactotron-react-js'

import { API_DEPRECATED } from 'Api_DEPRECATED'
import { IRootStore } from 'Models'

// start with the bare reactotron type definition
type VanillaReactotron = typeof Reactotron

// extend the type with features bestowed by plugins
// eslint-disable-next-line @typescript-eslint/naming-convention
export interface Reactotron extends VanillaReactotron {
  trackMstNode: (mstNode: any, name?: string) => any
  // Add any extra plugins here
}

export class LogConfig {
  private static enabled = false
  private static rootStore: IRootStore | undefined = undefined

  public static configure = (enabled: boolean): void => {
    LogConfig.enabled = enabled

    if (enabled) {
      LogConfig.configureReactotron()
      LogConfig.connectConsoleToReactotron()

      // REFERENCE: You can use the following code to create custom commands.
      /* Reactotron.onCustomCommand({
      //   command: 'Example command',
      //   handler: () => console.log('This is an example')
         })
      */
      Reactotron.onCustomCommand({
        command: 'Show environment',
        description: 'Show all environment variables in the console log',
        handler: (): void => {
          console.log('environment variables', process.env)
        },
      })

      if (LogConfig.rootStore) {
        LogConfig.trackRootStore(LogConfig.rootStore)
      }
    }
  }

  public static setRootStore = (rootStore: IRootStore): void => {
    LogConfig.rootStore = rootStore

    if (LogConfig.enabled) {
      // Let reactotron-mst know about the rootStore from Mobx-State-Tree
      LogConfig.trackRootStore(LogConfig.rootStore)
    }
  }

  private static trackRootStore = (rootStore: IRootStore): void => {
    if (Reactotron.trackMstNode) {
      Reactotron.trackMstNode(rootStore)
    }
  }

  private static display = (name: string, ...messages: unknown[]): void => {
    const [message] = messages
    const preview = typeof message !== 'string' ? JSON.stringify(message) : message
    Reactotron.display({
      name,
      preview,
      value: messages,
    })
  }

  private static configureReactotron = (): void => {
    Reactotron.configure({
      name: 'App',
    })
      .use(trackGlobalErrors({}))
      .use(mst())
      .use(apisaucePlugin()) // <-- here we go!!!
      .connect()

    API_DEPRECATED.http.addMonitor((Reactotron as any).apisauce)

    // clear log on start
    if (Reactotron.clear) {
      Reactotron.clear()
    }
  }

  private static connectConsoleToReactotron = (): void => {
    type TLogFn = (message: any, ...optionalParams: any[]) => void
    const runAll = (...fns: TLogFn[]): TLogFn => (message: any, ...optionalParams: any[]): void => {
      // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
      fns.map(fn => fn(message, ...optionalParams))
    }

    console.info = runAll(console.info, LogConfig.display.bind(null, 'INFO')) // eslint-disable-line no-console
    console.log = runAll(console.log, LogConfig.display.bind(null, 'LOG')) // eslint-disable-line no-console
    console.warn = runAll(console.warn, LogConfig.display.bind(null, 'WARN')) // eslint-disable-line no-console
    console.error = runAll(console.error, LogConfig.display.bind(null, 'ERROR')) // eslint-disable-line no-console
  }
}

export default LogConfig
